import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, LinearProgress, Pagination, Snackbar, Stack, Typography, Link, Button, TextareaAutosize } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { LeadsServiceAPI } from '../../shared/services/api/leads/LeadsService';
import { useDebounce } from '../../shared/hooks';
import { useAuth } from '../../shared/contexts';
import { LayoutBaseDePagina } from '../../shared/layouts';
import { FerramentasDaListagem } from '../../shared/components';
import { EstrategiaVendas } from '../sobre-projeto/SobreProjeto';

export const ListagemDeLeads: React.FC = () => {
    const { getUser } = useAuth();
    const [searchParams, setSearchParams] = useSearchParams();
    const { debounce } = useDebounce();
    const navigate = useNavigate();

    const [rows, setRows] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [totalCount, setTotalCount] = useState(0);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const [expandedCard, setExpandedCard] = useState<string | null>(null); // Para controlar o card expandido individualmente
    const [textareaValue, setTextareaValue] = useState(''); // Para armazenar o valor do campo de nota

    const busca = useMemo(() => searchParams.get('busca') || '', [searchParams]);
    const pagina = useMemo(() => Number(searchParams.get('pagina') || '1'), [searchParams]);

    useEffect(() => {
        loadLeads();
    }, [busca, pagina, getUser]);


    const loadLeads = () => {
        setIsLoading(true);
    
        debounce(() => {
            LeadsServiceAPI.getLeads(getUser())
                .then((result) => {
                    setIsLoading(false);
                    if (result instanceof Error) {
                        setSnackbarMessage(result.message);
                        setSnackbarOpen(true);
                    } else {
                        setTotalCount(result.data.length);
                        setRows(result.data);
                    }
                })
                .catch((err) => {
                    setIsLoading(false);
                    setSnackbarMessage(err.message);
                    setSnackbarOpen(true);
                });
        });
    };
    

    const handleSave = (leadId: string) => {
        LeadsServiceAPI.addNote(getUser(), leadId, textareaValue)
            .then(() => {
                setSnackbarMessage('Salvo com sucesso!');
                setSnackbarOpen(true);
                setExpandedCard(null);  // Fechar o card após salvar
                loadLeads();
            })
            .catch((err) => {
                setSnackbarMessage(`Erro ao salvar: ${err.message}`);
                setSnackbarOpen(true);
            });
    };

    const handleExpandCard = (leadId: string) => {
        setExpandedCard(expandedCard === leadId ? null : leadId);
        const lead = rows.find((lead) => lead.id === leadId);
        if (lead) {
            setTextareaValue(lead.note || ''); // Preenche o textarea com a nota do lead
        }
    };

    return (
        <LayoutBaseDePagina
            titulo="Listagem de Leads"
            barraDeFerramentas={
                <FerramentasDaListagem
                    mostrarInputBusca
                    textoDaBusca={busca}
                    aoMudarTextoDeBusca={(texto) => setSearchParams({ busca: texto, pagina: '1' }, { replace: true })}
                />
            }
        >
            <Stack direction="row" spacing={2} flexWrap="wrap" justifyContent="space-between" alignItems="center">
                {isLoading ? (
                    <LinearProgress sx={{ width: '100%' }} />
                ) : (
                    rows.map((lead) => (
                        <Card
                            key={lead.phoneNumber}
                            sx={{
                                maxWidth: 300,
                                margin: 16,
                            }}
                        >
                            <CardHeader
                                title={<Typography variant="h6" sx={{ textAlign: 'center', fontWeight: 'bold' }}>{lead.name}</Typography>}
                                subheader={<Typography variant="body2" sx={{ textAlign: 'center', color: 'text.secondary' }}>{lead.phoneNumber}</Typography>}
                                sx={{ paddingBottom: 0 }}
                            />
                            <CardContent>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>URL:</strong>{' '}
                                    <Link
                                        href={lead.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        color="primary"
                                        sx={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: 'calc(100% - 30px)' }}
                                    >
                                        {lead.url.length > 30 ? `${lead.url.slice(0, 30)}...` : lead.url}
                                    </Link>
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    <strong>Endereço:</strong> {lead.address}
                                </Typography>
                                <Link
                                    href={`https://wa.me/${lead.phoneNumber.replace(/\D/g, '')}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ textDecoration: 'none', color: 'green', fontWeight: 'bold' }}
                                >
                                    <Typography variant="body2">Iniciar conversa no WhatsApp</Typography>
                                </Link>

                                <Stack direction="column" spacing={1} sx={{ marginTop: 2 }}>
                                    <Button
                                        variant="outlined"
                                        onClick={() => handleExpandCard(lead.id)}  // Controla expansão por card
                                        sx={{ width: '100%' }}
                                    >
                                        {expandedCard === lead.phoneNumber ? 'Fechar' : 'Adicionar nota'}
                                    </Button>

                                    <EstrategiaVendas />
                                    {expandedCard === lead.id && (
                                        <>
                                            <TextareaAutosize
                                                minRows={3}
                                                placeholder="Digite sua nota"
                                                value={textareaValue}  // Preenche com a nota do lead
                                                onChange={(e) => setTextareaValue(e.target.value)}
                                                style={{
                                                    width: '100%',
                                                    padding: '8px',
                                                    fontSize: '14px',
                                                    marginBottom: '8px',
                                                }}
                                            />
                                            <Button
                                                variant="contained"
                                                onClick={() => handleSave(lead.id)}
                                                sx={{ width: '100%' }}
                                            >
                                                Salvar
                                            </Button>
                                        </>
                                    )}
                                </Stack>
                            </CardContent>
                        </Card>
                    ))
                )}
            </Stack>

            <Pagination
                page={pagina}
                count={Math.ceil(totalCount / 10)}
                onChange={(_, newPage) => setSearchParams({ busca, pagina: newPage.toString() }, { replace: true })}
            />

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                message={snackbarMessage}
            />
        </LayoutBaseDePagina>
    );
};
